/**
 * Degradation Module
 * Handles system degradation status display and notifications
 */

import { API_ENDPOINTS } from '../config.js';
import { setDegradationStatus } from './state.js';

// Degradation status levels
const STATUS_LEVELS = {
  HEALTHY: 'healthy',
  DEGRADED: 'degraded',
  FAILED: 'failed',
};

// Check interval for degradation status
const CHECK_INTERVAL = 60000; // 1 minute

let statusCheckInterval = null;
let _currentStatus = STATUS_LEVELS.HEALTHY;
let statusBanner = null;

/**
 * Initialize degradation monitoring
 */
export function initDegradation() {
  // Create status banner container
  createStatusBanner();

  // Start monitoring
  checkDegradationStatus();

  // Set up periodic checks
  statusCheckInterval = setInterval(checkDegradationStatus, CHECK_INTERVAL);
}

/**
 * Create the degradation status banner
 */
function createStatusBanner() {
  // Check if banner already exists
  if (document.getElementById('degradation-banner')) {
    return;
  }

  // Create banner element
  statusBanner = document.createElement('div');
  statusBanner.id = 'degradation-banner';
  statusBanner.className = 'degradation-banner hidden';
  statusBanner.innerHTML = `
        <div class="degradation-content">
            <span class="degradation-icon">⚠️</span>
            <span class="degradation-message"></span>
            <button class="degradation-close" aria-label="Close">×</button>
        </div>
    `;

  // Add styles
  const style = document.createElement('style');
  style.textContent = `
        .degradation-banner {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 10000;
            background: linear-gradient(to bottom, #f39c12, #e67e22);
            color: white;
            padding: 10px;
            text-align: center;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
            transition: transform 0.3s ease;
        }

        .degradation-banner.hidden {
            transform: translateY(-100%);
        }

        .degradation-banner.error {
            background: linear-gradient(to bottom, #e74c3c, #c0392b);
        }

        .degradation-content {
            max-width: 1200px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
        }

        .degradation-icon {
            font-size: 20px;
        }

        .degradation-message {
            flex: 1;
            text-align: left;
        }

        .degradation-close {
            background: rgba(255, 255, 255, 0.2);
            border: none;
            color: white;
            font-size: 24px;
            cursor: pointer;
            padding: 0 10px;
            border-radius: 3px;
        }

        .degradation-close:hover {
            background: rgba(255, 255, 255, 0.3);
        }

        /* Stale data indicators */
        .stale-indicator {
            display: inline-block;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 11px;
            margin-left: 5px;
            font-weight: bold;
        }

        .stale-indicator.stale {
            background: #f39c12;
            color: white;
        }

        .stale-indicator.very_stale {
            background: #e67e22;
            color: white;
        }

        .stale-indicator.critical {
            background: #e74c3c;
            color: white;
        }

        .stale-indicator.fallback {
            background: #9b59b6;
            color: white;
        }

        /* Feature disabled overlay */
        .feature-disabled {
            position: relative;
            opacity: 0.5;
            pointer-events: none;
        }

        .feature-disabled::after {
            content: 'Temporarily Unavailable';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 10px 20px;
            border-radius: 5px;
            font-weight: bold;
            z-index: 100;
        }
    `;

  document.head.appendChild(style);
  document.body.appendChild(statusBanner);

  // Add close button handler
  statusBanner.querySelector('.degradation-close').addEventListener('click', () => {
    hideBanner();
  });
}

/**
 * Check system degradation status
 */
async function checkDegradationStatus() {
  try {
    const response = await fetch(API_ENDPOINTS.degradation + '/status');
    if (!response.ok) {
      throw new Error('Failed to fetch degradation status');
    }

    const status = await response.json();
    handleDegradationStatus(status);

    // Also check for message
    const messageResponse = await fetch(API_ENDPOINTS.degradation + '/message');
    if (messageResponse.ok) {
      const messageData = await messageResponse.json();
      if (messageData.has_message) {
        showBanner(messageData.message);
      } else {
        hideBanner();
      }
    }
  } catch (error) {
    console.error('[Degradation] Error checking status:', error);
  }
}

/**
 * Handle degradation status update
 */
function handleDegradationStatus(status) {
  _currentStatus = status.status;

  // Update state
  setDegradationStatus(status);

  // Update UI based on status
  updateUIForDegradation(status);

  // Check for failed services
  if (status.failed_services && status.failed_services.length > 0) {
    console.warn('[Degradation] Failed services:', status.failed_services);
  }

  // Check for degraded features
  if (status.degraded_features && status.degraded_features.length > 0) {
    console.warn('[Degradation] Degraded features:', status.degraded_features);
    disableDegradedFeatures(status.degraded_features);
  }
}

/**
 * Show the degradation banner
 */
function showBanner(message) {
  if (!statusBanner) {
    createStatusBanner();
  }

  const messageEl = statusBanner.querySelector('.degradation-message');
  messageEl.textContent = message.message || 'System operating in degraded mode';

  // Add details if available
  if (message.details) {
    messageEl.innerHTML += `<br><small>${message.details}</small>`;
  }

  // Set severity class
  statusBanner.classList.remove('error');
  if (message.severity === 'error') {
    statusBanner.classList.add('error');
  }

  // Show banner
  statusBanner.classList.remove('hidden');
}

/**
 * Hide the degradation banner
 */
function hideBanner() {
  if (statusBanner) {
    statusBanner.classList.add('hidden');
  }
}

/**
 * Update UI elements for degradation status
 */
function updateUIForDegradation(status) {
  // Update any status indicators
  const statusIndicators = document.querySelectorAll('.system-status');
  statusIndicators.forEach(indicator => {
    indicator.className = `system-status status-${status.status}`;
    indicator.setAttribute('title', `System: ${status.status}`);
  });

  // Update connection status if needed
  const connectionStatus = document.querySelector('.connectionStatus');
  if (connectionStatus && status.status !== STATUS_LEVELS.HEALTHY) {
    connectionStatus.classList.add('degraded');
  }
}

/**
 * Disable features that are degraded
 */
function disableDegradedFeatures(degradedFeatures) {
  degradedFeatures.forEach(feature => {
    if (feature.fallback === 'disable_feature') {
      // Find and disable UI elements for this feature
      const elements = document.querySelectorAll(`[data-feature="${feature.feature}"]`);
      elements.forEach(el => {
        el.classList.add('feature-disabled');
      });
    }
  });
}

/**
 * Add stale data indicator to an element
 */
export function addStaleIndicator(element, metadata) {
  if (!metadata) {
    return;
  }

  // Remove existing indicator
  const existing = element.querySelector('.stale-indicator');
  if (existing) {
    existing.remove();
  }

  // Check for stale or fallback data
  if (metadata.stale || metadata.fallback) {
    const indicator = document.createElement('span');
    indicator.className = 'stale-indicator';

    if (metadata.fallback) {
      indicator.classList.add('fallback');
      indicator.textContent = `CACHED ${metadata.age}`;
      indicator.title = 'Using cached data - service temporarily unavailable';
    } else if (metadata.stale) {
      indicator.classList.add(metadata.severity);
      indicator.textContent = `${metadata.age} old`;
      indicator.title = 'Data may be outdated';
    }

    element.appendChild(indicator);
  }
}

/**
 * Check if a feature is available
 */
export async function isFeatureAvailable(feature) {
  try {
    const response = await fetch(`${API_ENDPOINTS.degradation}/feature/${feature}`);
    if (!response.ok) {
      return true; // Assume available if we can't check
    }

    const data = await response.json();
    return data.available;
  } catch (error) {
    console.error(`[Degradation] Error checking feature ${feature}:`, error);
    return true; // Assume available on error
  }
}

/**
 * Clean up degradation monitoring
 */
export function cleanupDegradation() {
  if (statusCheckInterval) {
    clearInterval(statusCheckInterval);
    statusCheckInterval = null;
  }

  if (statusBanner) {
    statusBanner.remove();
    statusBanner = null;
  }
}

// Export for use in other modules
export default {
  init: initDegradation,
  cleanup: cleanupDegradation,
  checkStatus: checkDegradationStatus,
  addStaleIndicator,
  isFeatureAvailable,
};